class BlogGrid {
	constructor() {
		this.initialized = false;
		this.didPruneInitialExcessAfterFirstAppend = false;
		this.initialCardsCount = 0;
		this.container = null;
		this.grid = null;
		this.loadMoreContainer = null;
		this.loadMoreBtn = null;
		this.filterBtns = null;

		this.containerId = 'msup-blog-grid-container';
		this.filterActiveClass = 'msup-blog-filter-active';
		this.blogCardClass = 'msup-blog-card';
		this.blogCardInitialClass = 'msup-blog-card-initial';
		this.settings = window.MSUP_BLOG_GRID_SETTINGS || {};
		this.blogSlug = this.settings.blogSlug || '';
		this.breadcrumbsCategoryClass =
			this.settings.breadcrumbs?.categoryClass || '';
		this.breadcrumbsCategoryDelimiterClass =
			this.settings.breadcrumbs?.delimiterClass || '';
		this.breadcrumbsBlogTitleClass =
			this.settings.breadcrumbs?.blogTitleClass || '';
		this.filters = this.settings.filters || {};
	}

	init() {
		if (this.initialized) {
			return;
		}

		this.container = document.getElementById(this.containerId);

		if (!this.container) {
			return;
		}

		this.grid = this.container.querySelector('.msup-blog-grid');
		this.loadMoreContainer = this.container.querySelector(
			'.msup-blog-load-more-container'
		);
		this.loadMoreBtn = this.loadMoreContainer
			? this.loadMoreContainer.querySelector('.msup-blog-load-more')
			: null;
		this.filterBtns = this.container.querySelectorAll('.msup-blog-filter');

		this.initialCardsCount = this.getInitialCardsCount();

		this.setInitialLoadMoreVisibility();

		this.bindEvents();

		this.initialized = true;
	}

	bindEvents() {
		this.filterBtns.forEach((btn) => {
			btn.addEventListener('click', (e) => this.handleFilterClick(e));
		});

		if (this.loadMoreBtn) {
			this.loadMoreBtn.addEventListener('click', () =>
				this.handleLoadMore()
			);
		}

		window.addEventListener('popstate', (e) => this.handlePopState(e));
	}

	async handleFilterClick(event, updateUrl = true) {
		const btn = event.currentTarget;
		const catSlug = btn.dataset.slug || '';
		const catName = btn.textContent;

		// UI-Update
		this.filterBtns.forEach((b) =>
			b.classList.remove(this.filterActiveClass)
		);
		btn.classList.add(this.filterActiveClass);

		// Status-Update
		this.filters.category = btn.dataset.id || '';

		this.updateBreadcrumbs(catSlug, catName);

		if (updateUrl) {
			this.updateSlugInUrl(catSlug);
		}

		await this.fetchPosts(false);
	}

	async handleLoadMore() {
		await this.fetchPosts(true);
	}

	async fetchPosts(append = false) {
		if (!append) {
			this.grid.innerHTML = '<div class="msup-blog-spinner"></div>';
			this.initialCardsCount = 0;
			this.updateLoadMoreVisibility(false);
		}

		this.container.setAttribute('aria-busy', 'true');
		this.setLoadMoreDisabled(true);
		this.setFiltersDisabled(true);

		const url = this.buildRestUrl();

		try {
			const response = await fetch(url, {
				headers: { 'X-WP-Nonce': this.settings.rest.nonce },
			});

			if (!response.ok) {
				throw new Error('Netzwerk-Antwort war nicht ok');
			}

			const data = await response.json();

			if (!data || typeof data.html === 'undefined') {
				throw new Error('Ungültige Datenstruktur vom Server erhalten');
			}

			if (append) {
				this.grid.insertAdjacentHTML('beforeend', data.html);

				if (
					!this.didPruneInitialExcessAfterFirstAppend &&
					this.initialCardsCount > 0
				) {
					this.pruneInitialCardsToCurrentPerPage();
					this.didPruneInitialExcessAfterFirstAppend = true;
				}
			} else {
				this.grid.innerHTML = data.html;
			}

			this.updateLoadMoreVisibility(data.hasMorePosts);
		} catch (e) {
			console.error('MSuP Blog: Fetch error:', e);
		} finally {
			this.container.setAttribute('aria-busy', 'false');
			this.setLoadMoreDisabled(false);
			this.setFiltersDisabled(false);
		}
	}

	handlePopState(event) {
		let categoryId = '';

		// Falls ein State vorhanden ist, nutzen wir die gespeicherte ID
		// Falls kein State vorhanden ist (Initialzustand), bleibt categoryId ""
		if (event.state && typeof event.state.categoryId !== 'undefined') {
			categoryId = String(event.state.categoryId);
		}

		const targetBtn = Array.from(this.filterBtns).find(
			(btn) => (btn.dataset.id || '') === categoryId
		);

		if (targetBtn) {
			this.handleFilterClick({ currentTarget: targetBtn }, false);
		}
	}

	updateSlugInUrl(newSlug) {
		if (!this.blogSlug) {
			return;
		}

		let newUrl = window.location.origin + `/${this.blogSlug}/`;

		if (newSlug) {
			newUrl += newSlug + '/';
		}

		window.history.pushState(
			{ categoryId: this.filters.category || '' },
			'',
			newUrl
		);
	}

	updateBreadcrumbs(catSlug, catName) {
		const breadcrumbContainer = document.querySelector(
			'#breadcrumbs #crumbs'
		);

		if (
			!breadcrumbContainer ||
			!this.breadcrumbsCategoryClass ||
			!this.breadcrumbsCategoryDelimiterClass ||
			!this.breadcrumbsBlogTitleClass
		) {
			return;
		}

		const blogTitleElement = breadcrumbContainer.querySelector(
			`.${this.breadcrumbsBlogTitleClass}`
		);

		// Blog-Titel als Link definieren, sobald eine Kategorie ausgewählt wurde
		if (blogTitleElement) {
			if (catSlug && blogTitleElement.tagName === 'SPAN') {
				// Umwandeln in Link
				const link = document.createElement('a');
				link.className = this.breadcrumbsBlogTitleClass;
				link.href = window.location.origin + `/${this.blogSlug}/`;
				link.textContent = blogTitleElement.textContent;
				blogTitleElement.replaceWith(link);
			} else if (!catSlug && blogTitleElement.tagName === 'A') {
				// Umwandeln in Text
				const span = document.createElement('span');
				span.className = this.breadcrumbsBlogTitleClass;
				span.textContent = blogTitleElement.textContent;
				blogTitleElement.replaceWith(span);
			}
		}

		// Bestehende Kategorie-Ergänzung entfernen (falls vorhanden)
		const existingCat = breadcrumbContainer.querySelector(
			`.${this.breadcrumbsCategoryClass}`
		);
		const existingDelimiter = breadcrumbContainer.querySelector(
			`.${this.breadcrumbsCategoryDelimiterClass}`
		);

		if (existingCat) {
			existingCat.remove();
		}

		if (existingDelimiter) {
			existingDelimiter.remove();
		}

		// Wenn eine Kategorie gewählt wurde (nicht "Alle")
		if (catSlug) {
			const delimiter = document.createElement('i');
			delimiter.classList.add(
				'fa-angle-right',
				'crumbs_delimiter',
				this.breadcrumbsCategoryDelimiterClass
			);

			const category = document.createElement('span');
			category.className = this.breadcrumbsCategoryClass;
			category.textContent = catName;

			breadcrumbContainer.append(' ', delimiter, ' ', category);
		}
	}

	pruneInitialCardsToCurrentPerPage() {
		const perPage = this.getCurrentPerPage();
		const initialCards = Array.from(
			this.grid.querySelectorAll(`.${this.blogCardInitialClass}`)
		);

		const excess = initialCards.length - perPage;
		if (excess <= 0) {
			return;
		}

		initialCards.slice(perPage).forEach((el) => el.remove());
		this.initialCardsCount = Math.max(0, this.initialCardsCount - excess);
	}

	buildRestUrl() {
		const perPage = this.getCurrentPerPage();
		const allCards = this.grid.querySelectorAll(`.${this.blogCardClass}`);
		const newCardsCount = allCards.length - this.initialCardsCount;

		const visibleInitialCount = Math.min(this.initialCardsCount, perPage);
		const offset = visibleInitialCount + newCardsCount;

		const url = new URL(`${this.settings.rest.base}/posts`);

		url.searchParams.set('per_page', String(perPage));
		url.searchParams.set('offset', String(offset));

		Object.keys(this.filters).forEach((key) => {
			if (this.filters[key]) {
				url.searchParams.set(key, String(this.filters[key]));
			}
		});

		return url.toString();
	}

	updateLoadMoreVisibility(hasMore) {
		if (this.loadMoreContainer) {
			this.loadMoreContainer.style.display = hasMore ? '' : 'none';
		}
	}

	setInitialLoadMoreVisibility() {
		const hasMoreFromServer =
			this.settings.hasMorePosts === true ||
			this.settings.hasMorePosts === '1' ||
			this.settings.hasMorePosts === 1;

		const limit = this.getCurrentPerPage();
		const hasHiddenInitialCards = this.initialCardsCount > limit;

		if (hasMoreFromServer || hasHiddenInitialCards) {
			this.updateLoadMoreVisibility(true);
		}
	}

	setFiltersDisabled(disabled) {
		this.filterBtns.forEach((btn) => {
			btn.disabled = disabled;
			btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
		});
	}

	setLoadMoreDisabled(disabled) {
		if (!this.loadMoreBtn) {
			return;
		}

		this.loadMoreBtn.disabled = disabled;
		this.loadMoreBtn.setAttribute(
			'aria-disabled',
			disabled ? 'true' : 'false'
		);
	}

	getInitialCardsCount() {
		return this.grid.querySelectorAll(`.${this.blogCardInitialClass}`)
			.length;
	}

	getCurrentPerPage() {
		const width = window.innerWidth;
		const breakpointTablet = 1035;
		const breakpointMobile = 600;
		let perPage = this.settings.perPage.desktop;

		if (width <= breakpointMobile) {
			perPage = this.settings.perPage.mobile;
		} else if (width <= breakpointTablet) {
			perPage = this.settings.perPage.tablet;
		}

		return parseInt(perPage, 10);
	}
}

export const blogGrid = new BlogGrid();
